import { Injectable } from '@angular/core';
import { AppFilterStorageKey, ISessionState, SessionStateStorage } from '../types/common';
import { FacetedFilterResultBuilder } from '../Utils/faceted-filter-result.builder';
import * as FilterPanelConstants from '../types/constants';
import {
  IDateRangeValue,
  IFacetedFilterData,
  IFacetedFilterDataItem,
  IFacetedFilterSelection,
  IHierarchyValue,
  ISelectionInfo,
} from '../types/faceted-filter.types';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { TypeCheck } from '../Utils/type-check';
import { Params, Router } from '@angular/router';
import { IFilterWellViewModel, IFilterWellConfig, IFilterWellDefaults } from './filterwell.types';
import { ConfigService } from '@core/services';

@Injectable({ providedIn: 'root' })
export class FilterWellFacade {
  private _viewModel = new BehaviorSubject<IFilterWellViewModel>({} as IFilterWellViewModel);
  viewModel$: Observable<IFilterWellViewModel> = this._viewModel.asObservable();

  private _keySuffix = AppFilterStorageKey.listviewFilterwell;
  private _key = '';
  private _storageKey = '';
  private _filterDataKeySuffix = AppFilterStorageKey.listviewFilterwellData;
  private _filterDataKey = '';
  private _filterDataStorageKey = '';
  private _disregardedStorageKeySuffix = '.disregarded.default';
  private _disregardedDefaultDataKey = '';
  private _storage = new SessionStateStorage();
  private _cacheLifeInMinutes: number = 20; // TODO: Get this from config
  private _activeQueryParams: string[] = [];
  private _configDefaultFilterSelections: IFacetedFilterSelection[] = [];
  private serviceUri: string = '';

  constructor(
    private router: Router,
    private http: HttpClient,
    private config: ConfigService
  ) {
    this.config.Config.subscribe((config) => {
      if (config?.apiUri) {
        this.serviceUri = `${config.apiUri}/filter/`;
      }
    });
  }

  async getData(): Promise<void> {
    const myConfig: IFilterWellConfig = {
      context: '',
      contextPath: '',
      defaultFilters: [],
    };
    console.debug('FilterWellFacade.getData.withConfig', myConfig);
    this.setDataKeys('crs-data');
    this.setStorageKeys();
    this.getViewModelData(myConfig);
  }

  setState(filters: IFacetedFilterSelection[], key?: string): void {
    const storageKey = key ?? this._storageKey;
    const stateData: ISessionState = {
      date: new Date(),
      data: filters,
    };
    this._storage.set(storageKey, stateData);
  }

  clearState(): void {
    this._storage.remove(this._storageKey);
    this._storage.remove(this._disregardedDefaultDataKey);
  }

  clearData(): void {
    this._viewModel.next({} as IFilterWellViewModel);
  }

  setDefaultAsDisregarded(data: IFacetedFilterSelection): void {
    let current = this.getCachedSelections(this._disregardedDefaultDataKey);
    if (current && current.length) current.push(data);
    else current = [data];

    this.setState(current, this._disregardedDefaultDataKey);
  }

  isDisregardedDefault(data: IFacetedFilterSelection): boolean {
    const disregardedDefaultCache = this.getCachedSelections(this._disregardedDefaultDataKey);
    return !!disregardedDefaultCache?.find((ddc) => ddc.path === data.path);
  }

  getNondisregardedDefaults(data: IFacetedFilterSelection[]): IFacetedFilterSelection[] {
    const disregardedDefaultCache = this.getCachedSelections(this._disregardedDefaultDataKey);
    const nonDisregards: IFacetedFilterSelection[] = [];

    if (!disregardedDefaultCache?.length) return data;

    data.forEach((d) => {
      if (!disregardedDefaultCache.find((ddc) => ddc.path === d.path)) nonDisregards.push(d);
    });

    return nonDisregards;
  }

  private setStorageKeys(): void {
    this._storageKey = `${window.location.pathname.replace(/\//g, '.')}.${this._key}`;
    this._filterDataStorageKey = `${window.location.pathname.replace(/\//g, '.')}.${this._filterDataKey}`;
    this._disregardedDefaultDataKey = `${this._filterDataStorageKey}.${this._disregardedStorageKeySuffix}`;
  }

  private setDataKeys(appConfigContext: string): void {
    this._key = `${appConfigContext}.${this._keySuffix}`;
    this._filterDataKey = `${appConfigContext}.${this._filterDataKeySuffix}`;
  }

  private setBaseDataUri(_config: IFilterWellConfig): void {
    // if (!ENV.isProd)
    //     this._baseUri = config.localContextRoot ?? ENV.urls.filterContextRoot;
    // else
    //     this._baseUri = ENV.urls.filterContextRoot;
  }

  private setNoOpResult(): void {
    const noOpModel: IFilterWellViewModel = {
      filterData: { label: '', items: [] },
      defaultFilters: [],
      cachedFilters: [],
      urlFilters: [],
      config: { context: '', contextPath: '', defaultFilters: [] },
      noData: true,
    };
    this._viewModel.next(noOpModel);
  }

  private cacheFilterData(data: IFacetedFilterData): void {
    const stateData: ISessionState = {
      date: new Date(),
      data: data,
    };
    this._storage.set(this._filterDataStorageKey, stateData);
  }

  private getCachedFilterData(): IFacetedFilterData | undefined {
    const stateData: ISessionState = this._storage.get(this._filterDataStorageKey);
    if (!Object.keys(stateData).length) return undefined;

    const now = new Date();
    const stateDate: Date = new Date(stateData.date);
    const diff = now.getTime() - stateDate.getTime();
    const lifeTime = Math.round(diff / 60000);
    if (lifeTime < this._cacheLifeInMinutes) return stateData.data as IFacetedFilterData;
    else {
      this._storage.remove(this._filterDataStorageKey);
      return undefined;
    }
  }

  private convertToConfig(value: string): IFilterWellConfig {
    return JSON.parse(value) as IFilterWellConfig;
  }

  private getViewModelData(config: IFilterWellConfig): void {
    this.getContextData(config).subscribe((filterData) => {
      if (!filterData) {
        this.setNoOpResult();
        return;
      }
      this._configDefaultFilterSelections = this.getDefaultFilterSelectionsNotAlreadyInCache(config, filterData.items);
      const model: IFilterWellViewModel = {
        filterData,
        config,
        defaultFilters: [...this._configDefaultFilterSelections],
        // urlFilters     : [...this.getUrlFilterSelections(filterData.items, routeInfo)],
        urlFilters: [],
        cachedFilters: [...this.getCachedSelections()],
      };

      this._viewModel.next(model);
    });
  }

  private getContextData(_config: IFilterWellConfig): Observable<IFacetedFilterData | undefined> {
    if (!this.serviceUri) {
      return of(undefined);
    }
    const cachedFilterData = this.getCachedFilterData();
    if (cachedFilterData) {
      return of(cachedFilterData);
    }

    const uri: string = `${this.serviceUri}getFilters`;
    return this.http.post<IFacetedFilterData>(uri, '').pipe(
      tap({
        next: (response) => this.cacheFilterData(response),
        error: () => this.setNoOpResult(),
      })
    );
  }

  private getCachedSelections(key?: string): IFacetedFilterSelection[] {
    const storagekey = key ?? this._storageKey;
    const state = this._storage.get(storagekey);
    const stateFilters = state.data as IFacetedFilterSelection[];

    return stateFilters?.length ? [...stateFilters] : [];
  }

  private getDefaultFilterSelectionsNotAlreadyInCache(
    config: IFilterWellConfig,
    filterDataItems: IFacetedFilterDataItem[]
  ): IFacetedFilterSelection[] {
    return config.defaultFilters.reduce((selections, filter) => {
      const selection = this.toSelection(filter, filterDataItems);
      if (selection) {
        this.checkCacheForStaleDefaultDateFilter(selection);
        selection.isDefault = true;
        const exists = !![...selections, ...this.getCachedSelections()].find((s) => compareSelections(s, selection));

        return exists ? selections : selections.concat(selection);
      } else {
        return selections;
      }
    }, [] as IFacetedFilterSelection[]);
  }

  // private getUrlFilterSelections(filterDataItems: IFacetedFilterDataItem[], routeInfo: SerializedRouterStateSnapshot): IFacetedFilterSelection[] {
  //     let filters: IFilterWellDefaults[] = [];

  //     this.setSegmentDefaultFromQueryParams(routeInfo?.root?.queryParams, filterDataItems, filters);
  //     this.setHierarchyDefaultFromQueryParams(routeInfo?.root?.queryParams, filterDataItems, filters);
  //     this.setEncodedDefaultsFromQueryParams(routeInfo?.root?.queryParams, filters);

  //     if (this._activeQueryParams.length)
  //         this.cleanUrl();

  //     if (!filters.length)
  //         return [];

  //     // Set config defaults as disregarded
  //     this._configDefaultFilterSelections.forEach(cd => this.setDefaultAsDisregarded(cd));

  //     return filters.reduce(
  //         (selections, filter) => {
  //             const selection = this.toSelection(filter, filterDataItems);
  //             if (selection) {
  //                 const exists = !!selections.find(s => compareSelections(s, selection));

  //                 return exists ? selections : selections.concat(selection);
  //             }
  //             return selections;
  //         },
  //         [] as IFacetedFilterSelection[]
  //     );
  // }

  private setEncodedDefaultsFromQueryParams(params: Params, defaults: IFilterWellDefaults[]): void {
    const encodedFilters: string = params?.filters;
    if (!encodedFilters) return;

    const decodedFilters = this.decodeFilters(encodedFilters);
    if (decodedFilters?.length) {
      defaults.push(...decodedFilters);
    }

    this._activeQueryParams.push('filters');
  }

  private setSegmentDefaultFromQueryParams(
    params: Params,
    filterDataItems: IFacetedFilterDataItem[],
    defaults: IFilterWellDefaults[]
  ): void {
    const segmentPath: string = params?.['segmentPath'];
    if (!segmentPath) return;

    const paths = segmentPath.split('|');
    const segments = filterDataItems.find((f) => f.label === paths[0]);
    if (segments?.items) {
      const segmentContainer = segments.items.find((f) => f.label === paths[1]);
      const defaultSegment = segmentContainer?.items?.length
        ? this.findSegmentFacet(segmentPath, segmentContainer.items)
        : undefined;
      if (defaultSegment) defaults.push(defaultSegment);
    }

    this._activeQueryParams.push('segmentPath');
  }

  private setHierarchyDefaultFromQueryParams(
    params: Params,
    filterDataItems: IFacetedFilterDataItem[],
    defaults: IFilterWellDefaults[]
  ): void {
    const hierarchyPath = params?.['hierarchyPath'];
    const hierarchyValue = params?.['hierarchyValue'];
    if (!hierarchyPath || !hierarchyValue) return;

    const paths = hierarchyPath.split('|');
    const hierarchies = filterDataItems.find((f) => f.label === paths[0]);
    const hierarchyContainer = hierarchies?.items?.find((h) => h.label === paths[1]);

    if (hierarchyContainer?.items?.length) {
      for (let i = 0; i < hierarchyContainer.items.length; i++) {
        const item = hierarchyContainer.items[i];
        if (item?.items) {
          const foundFilter = this.findRetailerFacet(hierarchyValue, item.items);
          if (foundFilter) {
            defaults.push(foundFilter);
            break;
          }
        }
      }
    }
    this._activeQueryParams.push(...['hierarchyPath', 'hierarchyValue']);
  }

  private findSegmentFacet(path: string, items: IFacetedFilterDataItem[]): IFilterWellDefaults | undefined {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;

      if (item.path !== path) {
        if (item.items?.length) {
          const result = this.findSegmentFacet(path, item.items);
          if (result) return result;
        }
      } else {
        return {
          path: item.path ?? '',
          valueType: item.valueType ?? '',
          value: [item.value as string],
        };
      }
    }
    return undefined;
  }

  private findRetailerFacet(retailerId: string, items: IFacetedFilterDataItem[]): IFilterWellDefaults | undefined {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;

      if (TypeCheck.isHierarchyContainer(item.type)) {
        if (item.items) {
          const result = this.findRetailerFacet(retailerId, item.items);
          if (result) return result;
        }
      } else if (
        item.value &&
        typeof item.value === 'object' &&
        'key' in item.value &&
        (item.value as IHierarchyValue).key === retailerId
      ) {
        return {
          path: item.path ?? '',
          valueType: item.valueType ?? '',
          value: [item.value as IHierarchyValue],
        };
      }
    }
    return undefined;
  }

  private toSelection(
    filter: IFilterWellDefaults,
    CONFIG_ITEMS: IFacetedFilterDataItem[]
  ): IFacetedFilterSelection | undefined {
    const paths = filter.path.split('|');
    const rootPath = paths.shift();
    const root = CONFIG_ITEMS.find((fdi) => fdi.path === rootPath);
    if (!root) return undefined;

    const existing = this.findFilter(paths, root, filter.value);
    if (existing) {
      const selection = FacetedFilterResultBuilder.buildSelectionResult(existing);
      return selection;
    }

    return undefined;
  }

  private decodeFilters(encodedFilters: string): IFilterWellDefaults[] {
    try {
      return encodedFilters ? JSON.parse(atob(encodedFilters)) : [];
    } catch {
      console.error('Error decoding filters, not using filters from url.');
      return [];
    }
  }

  private findFilter(
    paths: string[],
    filterDataItem: IFacetedFilterDataItem,
    values?: unknown
  ): ISelectionInfo | undefined {
    if (filterDataItem.items?.length) {
      const node = filterDataItem.items.find((fdi) => fdi.label === paths[0]);
      if (node) {
        let selectedChildren: IFacetedFilterDataItem[] | undefined;
        let selectedChild: IFacetedFilterDataItem | undefined;
        let isHierarchiesParent = false;
        if (values) {
          if (Array.isArray(values)) {
            if (TypeCheck.isHierarchyContainer(node.type)) {
              const hierarchyValues = values as IHierarchyValue[];
              const hvValues = hierarchyValues.map((hvv) => hvv.value);
              isHierarchiesParent = (node.items?.filter((i) => hvValues.includes(i.label)).length ?? 0) > 1;
            } else {
              selectedChildren = node.items?.filter((i) => (values as unknown[]).includes(i.value));
            }
          }
        } else {
          selectedChild = node?.items?.find((n) => n.label === paths[1]);
        }

        if (isHierarchiesParent) {
          return {
            item: node,
            hierarchies: [...(values as IHierarchyValue[])],
          };
        } else if (selectedChildren?.length) {
          return {
            item: node,
            facets: [...selectedChildren],
          };
        } else if (selectedChild && TypeCheck.isFacet(selectedChild.type)) {
          return {
            item: selectedChild,
            facets: [selectedChild],
          };
        } else {
          paths.shift();
          return this.findFilter(paths, node, values);
        }
      }
    } else {
      switch (filterDataItem.valueType) {
        case FilterPanelConstants.FacetedFilterItemValueType.DateRangeValue:
          return {
            item: filterDataItem,
            dateRange: values as IDateRangeValue,
          };
        case FilterPanelConstants.FacetedFilterItemValueType.OperatorValue:
          // TODO: Need to determine query param shape for this!!
          break;
        case FilterPanelConstants.FacetedFilterItemValueType.RetailerValue:
          return {
            item: filterDataItem,
            hierarchies: values as IHierarchyValue[],
          };
      }
    }
    return undefined;
  }

  private checkCacheForStaleDefaultDateFilter(current: IFacetedFilterSelection): void {
    if (current.payload?.valueType === FilterPanelConstants.FacetedFilterItemValueType.DateRangeValue) {
      let cached = [...this.getCachedSelections()];
      const cachedFilterOfSameDateType = cached.find((cs) => cs.path === current.path);
      if (cachedFilterOfSameDateType) {
        cached = cached.filter((c) => c.path !== cachedFilterOfSameDateType.path);
        this._storage.remove(this._filterDataStorageKey);

        this.setState(cached);
      }
    }
  }

  private cleanUrl(): void {
    // this.ngUtils.removeQueryParams(this._activeQueryParams);
  }
}

export function compareSelections(a: IFacetedFilterSelection, b: IFacetedFilterSelection): boolean {
  return a.path === b.path && a.displayValue === b.displayValue && a.payload?.value === b.payload?.value;
}
